import { createRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { z } from 'zod';
import { rootRoute } from './root.js';
import { registerOrg } from '../api/auth.js';
import { isAuthenticated, useAuth } from '../auth/AuthContext.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /register — self-register an organization + owner account.
 *
 * Flow: form → POST /v1/auth/register-org → POST /v1/auth/login → /dashboard.
 * This is the first UI entry point for a brand-new tenant; before this PR
 * you had to curl the endpoint.
 *
 * Already-authenticated visitors bounce to /dashboard, matching the login
 * route's behavior so the two are symmetric.
 */

const RegisterForm = z
  .object({
    orgSlug: z
      .string()
      .min(3)
      .max(63)
      .regex(
        /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
        'Lowercase letters, digits, and hyphens only',
      ),
    legalName: z.string().min(1).max(255),
    countryCode: z.enum(['NG', 'GH', 'CD']),
    baseCurrency: z.enum(['NGN', 'GHS', 'CDF', 'USD']),
    email: z.string().email().max(255),
    password: z.string().min(8, 'At least 8 characters').max(128),
  });
type RegisterForm = z.infer<typeof RegisterForm>;

export const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: RegisterPage,
});

function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(RegisterForm),
    defaultValues: {
      orgSlug: '',
      legalName: '',
      countryCode: 'NG',
      baseCurrency: 'NGN',
      email: '',
      password: '',
    },
  });

  async function onSubmit(values: RegisterForm): Promise<void> {
    setServerError(null);
    try {
      await registerOrg({
        org: {
          slug: values.orgSlug,
          legalName: values.legalName,
          countryCode: values.countryCode,
          baseCurrency: values.baseCurrency,
          // Sensible defaults for Sprint 0. A later PR adds the locale +
          // timezone pickers — right now we pick from the country.
          locale: LOCALE_BY_COUNTRY[values.countryCode],
          timezone: TIMEZONE_BY_COUNTRY[values.countryCode],
        },
        owner: {
          email: values.email,
          password: values.password,
        },
      });
      // Register succeeded; immediately log in to capture a token.
      await login({
        orgSlug: values.orgSlug,
        email: values.email,
        password: values.password,
      });
      await navigate({ to: '/dashboard' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError(
          'That org slug or email is already in use. Pick a different slug, or sign in if the account is yours.',
        );
      } else if (err instanceof ApiError && err.status === 429) {
        setServerError('Too many register attempts. Wait a minute and try again.');
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Registration failed. Try again.');
      }
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-md space-y-6 p-8">
        <header className="text-center space-y-2">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <p className="text-sm text-gray-400">Create a new workspace</p>
        </header>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field
            id="orgSlug"
            label="Workspace slug *"
            placeholder="my-company"
            autoComplete="off"
            error={errors.orgSlug?.message}
            inputProps={register('orgSlug')}
          />
          <Field
            id="legalName"
            label="Legal name *"
            placeholder="My Company Ltd"
            error={errors.legalName?.message}
            inputProps={register('legalName')}
          />

          <div className="grid grid-cols-2 gap-3">
            <SelectField
              id="countryCode"
              label="Country *"
              error={errors.countryCode?.message}
              inputProps={register('countryCode')}
              options={[
                { value: 'NG', label: 'Nigeria' },
                { value: 'GH', label: 'Ghana' },
                { value: 'CD', label: 'DR Congo' },
              ]}
            />
            <SelectField
              id="baseCurrency"
              label="Report currency *"
              error={errors.baseCurrency?.message}
              inputProps={register('baseCurrency')}
              options={[
                { value: 'NGN', label: 'NGN' },
                { value: 'GHS', label: 'GHS' },
                { value: 'CDF', label: 'CDF' },
                { value: 'USD', label: 'USD' },
              ]}
            />
          </div>

          <Field
            id="email"
            label="Owner email *"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            inputProps={register('email')}
          />
          <Field
            id="password"
            label="Password *"
            type="password"
            autoComplete="new-password"
            error={errors.password?.message}
            inputProps={register('password')}
          />

          {serverError ? <Alert variant="error">{serverError}</Alert> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create workspace'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          Already have a workspace?{' '}
          <Link to="/login" className="underline hover:text-white">
            Sign in
          </Link>
        </p>

        <p className="text-center text-xs text-gray-600">
          <Link to="/legal/data-retention" className="underline hover:text-gray-300">
            Data Retention &amp; Deletion Policy
          </Link>
        </p>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Country defaults. A Sprint 1 PR replaces these with a locale + timezone
// picker for multinational orgs. The current mapping is good enough for
// a first-time register flow.
// ---------------------------------------------------------------------------

const LOCALE_BY_COUNTRY: Record<'NG' | 'GH' | 'CD', string> = {
  NG: 'en-NG',
  GH: 'en-GH',
  CD: 'fr-CD',
};

const TIMEZONE_BY_COUNTRY: Record<'NG' | 'GH' | 'CD', string> = {
  NG: 'Africa/Lagos',
  GH: 'Africa/Accra',
  CD: 'Africa/Kinshasa',
};

// ---------------------------------------------------------------------------
// Field primitives (scoped to this route)
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<RegisterForm>>['register']>;
}

function Field({
  id,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  error,
  inputProps,
}: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

interface SelectFieldProps extends Omit<FieldProps, 'type' | 'placeholder' | 'autoComplete'> {
  options: Array<{ value: string; label: string }>;
}

function SelectField({ id, label, error, inputProps, options }: SelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
